<template>
  <loading-content :loading="loading">
    <!-- Süsteem -> Seaded -> Uued vaated -> Intra uus raam ja Töölaud: the same
         six lists in the hooldusplaat look, each card saying in words what is on
         it and what its number means, overdue and coming dates as pills instead
         of row tints. Same data, same limits, same "Vaata kõiki" pages. -->
    <div v-if="plate" class="plate-page">
      <div>
        <div class="plate-page__kicker">{{ todayLabel }}</div>
        <h1>Töölaud</h1>
      </div>
      <router-link v-if="today" to="/calendar/full" class="plate-today">
        <span class="d-inline-flex align-baseline ga-2">
          <span class="plate-today__count">{{ today.count }}</span>
          <span class="plate-today__label">{{ isTechnic ? 'sinu töökäsku täna' : 'töökäsku täna kalendris' }}</span>
        </span>
        <template v-if="today.people.length">
          <span class="plate-today__rule" />
          <span v-for="person in today.people" :key="person.key" class="plate-today__who">
            <span class="plate-dot" :style="person.color ? { background: person.color } : null">{{ person.initials }}</span>
            {{ person.workday }}: <b>{{ person.name }}</b>
          </span>
        </template>
        <span class="plate-today__go">Ava kalender →</span>
      </router-link>
      <p v-if="nothingWaiting" class="plate-empty">Hetkel pole midagi, mis ootaks.</p>
      <div class="plate-grid">
        <plate-card
          v-for="card in plateCards"
          :key="card.title"
          v-bind="card"
          :limit="rows"
        />
      </div>
    </div>
    <template v-else>
      <heading>Töölaud</heading>
      <!-- Empty cards take themselves off the page, so with nothing outstanding
           there would be no page left. Better one honest line than a blank. -->
      <p v-if="nothingWaiting" class="text-medium-emphasis">Hetkel pole midagi, mis ootaks.</p>
      <v-row>
        <!-- The field work first, oversight last: a technician's own backlog is
             what he opens this page for. -->
        <dashboard-card v-if="showAkts" title="Tegemata aktid" href="/akt/undone"
          :items="undoneAkts" :count="undoneAktsTotal" :limit="rows">
          <template #item="{ item }">
            <v-list-item :href="aktFormUrl(item)" lines="two" :class="overdueClass(item.daysSinceEvent)">
              <v-list-item-title>{{ objectAddress(item) }}</v-list-item-title>
              <v-list-item-subtitle>{{ day(item.startDate) }}<span v-if="eventTechnic(item)"> · {{ eventTechnic(item) }}</span></v-list-item-subtitle>
              <template #append>{{ item.daysSinceEvent }}</template>
            </v-list-item>
          </template>
        </dashboard-card>

        <dashboard-card v-if="showAkts" title="Lõpetamata aktid" href="/akt/started"
          :items="startedAkts" :count="startedAktsTotal" :limit="rows">
          <template #item="{ item }">
            <!-- Unfinished has more than one shape, and the akt module names them
                 in its own words: never signed, or signed and still owing one of
                 the two sends. -->
            <v-list-item :href="'/akt/' + item.id" lines="two">
              <v-list-item-title>{{ item.objectAddress || 'Aadress puudub' }}</v-list-item-title>
              <v-list-item-subtitle>{{ sendState(item) }}<span v-if="item.technicName"> · {{ item.technicName }}</span></v-list-item-subtitle>
              <template #append>{{ item.daysSinceVisit > 0 ? item.daysSinceVisit : '' }}</template>
            </v-list-item>
          </template>
        </dashboard-card>

        <dashboard-card title="Maksmata tööd" :to="unpaidLink"
          :items="unpaid" :count="unpaidTotal" :limit="rows">
          <template #item="{ item }">
            <v-list-item lines="two" :class="overdueClass(item.daysSinceEvent)">
              <v-list-item-title><object-link :object="item.object" /></v-list-item-title>
              <v-list-item-subtitle><a href="#" @click="viewEvent(item)">{{eventPeriod(item)}}</a></v-list-item-subtitle>
              <template #append>{{item.daysSinceEvent}}</template>
            </v-list-item>
          </template>
        </dashboard-card>

        <dashboard-card title="Hoolduse tähtaeg" :to="maintenenceLink"
          :items="maintenence" :count="maintenenceTotal" :limit="rows">
          <template #item="{ item }">
            <v-list-item :class="{
              'bg-red-lighten-4': item.latestInterval && item.latestInterval.daysTo < 0,
              'bg-yellow-lighten-4': item.latestInterval && item.latestInterval.daysTo >= 0
            }">
              <object-link :object="item" />
              <template #append>{{item.latestInterval && item.latestInterval.daysTo}}</template>
            </v-list-item>
          </template>
        </dashboard-card>

        <!-- Every row here is equally overdue, so tinting them all said nothing;
             the count in the header carries it instead. -->
        <dashboard-card title="Järelvaatajaga hooldus määramata" count-color="red-darken-2" :to="extraServiceLink"
          :items="nextExtraService" :count="nextExtraServiceTotal" :limit="rows">
          <template #item="{ item }">
            <v-list-item>
              <object-link :object="item" />
            </v-list-item>
          </template>
        </dashboard-card>

        <dashboard-card v-if="showLastLogins" title="Tehnikute sisselogimised" :to="lastLoginsLink"
          :items="lastLogins" :count="lastLoginsTotal" :limit="rows">
          <template #item="{ item }">
            <v-list-item :class="{
              'bg-red-lighten-4': item.daysLastLogin > 13,
              'bg-yellow-lighten-4': item.daysLastLogin <= 13
            }">
              <div class="d-flex">
                <span class="flex-grow-1">{{item.fullName}}</span>
                <span class="text-right">{{item.daysLastLogin}}</span>
              </div>
            </v-list-item>
          </template>
        </dashboard-card>
      </v-row>
    </template>
    <v-dialog v-model="selectedEvent" max-width="700px">
      <event mode="view" :eventId="selectedEventId" @close="selectedEvent = false" @updated="selectedEvent = false" />
    </v-dialog>
  </loading-content>
</template>

<script>
import api from './../api'
import dayjs from 'dayjs'
import { mapState } from 'vuex'
import helpers from '@/plugins/helpersMixin'
import PlateCard from '@/components/frame/PlateCard.vue'
import { initials } from '@/components/frame/frameLinks'

// How many rows a card shows. The lists behind them run to hundreds — the page
// says how much there is and leaves the reading of it to the page that owns it.
// It is also what each card now fetches, instead of the hundred rows it used to
// pull down and render into a list nobody scrolled.
const CARD_ROWS = 8

// Old enough that it stands out from the rest of its card. Both lists it
// applies to are already floored well below this by their own query.
const OVERDUE_DAYS = 30

// The window "Tegemata aktid" opens on in the akt module itself, so the number
// on the card is the number on the page it leads to.
const UNDONE_AKT_DAYS = 90

// The filters the four intra-side cards count with. Each one is used twice —
// once to fetch the card, once to build the "Vaata kõiki" link under it — so
// the page a card opens cannot come to disagree with the number in its header.
const UNPAID = { startDate: '2012-02-14', paymentType: 0, type: 2, category: [0, 1, 4, 6], daysSince: 7 }
const MAINTENENCE = { daysToNextMaintenence: 14, type: 1, active: 1 }
const EXTRA_SERVICE = { extraService: 1, nextExtraServiceExpired: true }
const LAST_LOGINS = { active: 1, role: 'tehnic', lastLogin: 6 }

export default {
  components: { PlateCard },
  mixins: [ helpers ],
  data () {
    return {
      loading: true,
      selectedEvent: false,
      selectedEventId: null,
      rows: CARD_ROWS,
      lastLogins: [],
      lastLoginsTotal: 0,
      unpaid: [],
      unpaidTotal: 0,
      nextExtraService: [],
      nextExtraServiceTotal: 0,
      maintenence: [],
      maintenenceTotal: 0,
      undoneAkts: [],
      undoneAktsTotal: 0,
      startedAkts: [],
      startedAktsTotal: 0,
      // The new look's "today" strip: { count, people }, or null until asked.
      today: null
    }
  },
  computed: {
    ...mapState(['user', 'features']),
    plate () {
      return !!this.features.intraFrame
    },
    todayLabel () {
      const label = dayjs().format('dddd, D. MMMM YYYY')
      return label.charAt(0).toUpperCase() + label.slice(1)
    },
    // The six cards of the new look, in the order and on the conditions the
    // original page uses. Each row says where it leads and what its number is.
    plateCards () {
      const days = (n, late) => ({ val: n + ' p', tone: n > late ? 'late' : null })
      const objectTo = object => object && object.id ? { name: 'object/view/info', params: { objectId: object.id } } : null
      const cards = []
      if (this.showAkts) {
        cards.push({
          title: 'Tegemata aktid',
          caption: 'Hooldus on tehtud, aga akt koostamata (viimased ' + UNDONE_AKT_DAYS + ' päeva). Päevi hooldusest; üle ' + OVERDUE_DAYS + ' päeva on oranž.',
          count: this.undoneAktsTotal,
          href: '/akt/undone',
          rows: this.undoneAkts.map(item => ({
            key: item.id,
            main: this.objectAddress(item),
            sub: [this.day(item.startDate), this.eventTechnic(item)].filter(Boolean).join(' · '),
            href: this.aktFormUrl(item),
            ...days(item.daysSinceEvent, OVERDUE_DAYS)
          }))
        }, {
          title: 'Lõpetamata aktid',
          caption: 'Akt on alustatud, aga allkiri või saatmine on pooleli. Päevi külastusest.',
          count: this.startedAktsTotal,
          href: '/akt/started',
          rows: this.startedAkts.map(item => ({
            key: item.id,
            main: item.objectAddress || 'Aadress puudub',
            sub: [this.sendState(item), item.technicName].filter(Boolean).join(' · '),
            href: '/akt/' + item.id,
            val: item.daysSinceVisit > 0 ? item.daysSinceVisit + ' p' : 'täna'
          }))
        })
      }
      cards.push({
        title: 'Maksmata tööd',
        caption: 'Töökäsk tehtud üle nädala tagasi, makse märkimata. Päevi tööst; üle ' + OVERDUE_DAYS + ' päeva on oranž.',
        count: this.unpaidTotal,
        to: this.unpaidLink,
        rows: this.unpaid.map(item => ({
          key: item.id,
          main: (item.object && this.$address(item.object.fullAddress)) || 'määramata',
          mainTo: objectTo(item.object),
          sub: this.eventPeriod(item),
          subClick: () => this.viewEvent(item),
          ...days(item.daysSinceEvent, OVERDUE_DAYS)
        }))
      }, {
        title: 'Hoolduse tähtaeg',
        caption: 'Lepingulise objekti hoolduse tähtaeg on möödas või 14 päeva sees.',
        count: this.maintenenceTotal,
        to: this.maintenenceLink,
        rows: this.maintenence.map(item => {
          const daysTo = item.latestInterval ? item.latestInterval.daysTo : null
          return {
            key: item.id,
            main: this.$address(item.fullAddress) || '-',
            to: objectTo(item),
            val: daysTo === null ? '' : daysTo < 0 ? -daysTo + ' p üle' : daysTo === 0 ? 'täna' : daysTo + ' p pärast',
            tone: daysTo === null ? null : daysTo < 0 ? 'late' : 'soon'
          }
        })
      }, {
        // Every row here is equally overdue, so the header count says so.
        title: 'Järelvaatajaga hooldus määramata',
        caption: 'Järelvaataja tähtaeg on möödas ja uut hooldusaega pole määratud.',
        count: this.nextExtraServiceTotal,
        countTone: 'late',
        to: this.extraServiceLink,
        rows: this.nextExtraService.map(item => ({
          key: item.id,
          main: this.$address(item.fullAddress) || '-',
          to: objectTo(item)
        }))
      })
      if (this.showLastLogins) {
        cards.push({
          title: 'Tehnikute sisselogimised',
          caption: 'Tehnikud, kes pole üle 6 päeva intrasse sisse loginud. Üle 13 päeva on oranž.',
          count: this.lastLoginsTotal,
          to: this.lastLoginsLink,
          rows: this.lastLogins.map(item => ({
            key: item.id,
            main: item.fullName,
            ...days(item.daysLastLogin, 13)
          }))
        })
      }
      return cards
    },
    showLastLogins () {
      return this.$hasRole(['admin', 'root'])
    },
    isTechnic () {
      return this.$hasRole(['tehnic']) && !this.$hasRole(['admin', 'secretary', 'root'])
    },
    // The akt module is optional, and its lists answer 403 to a role that may
    // not read them — this mirrors AKT_READ_ROLES on the server ('root' is
    // added there by requireRole itself). Both halves have to hold: no module,
    // no card; no role, no card.
    showAkts () {
      return !!this.features.akt && this.$hasRole(['tehnic', 'admin', 'secretary', 'root'])
    },
    // Pakkumised is a module of its own: with it off an act is finished once the
    // e-mail is out, and naming Merit would only ask for something that cannot
    // be done. Same reason the object's Aktid tab hides its Merit column.
    offersEnabled () {
      return !!this.features.offer
    },
    // Only a technician's cards are narrowed to his own work; the office
    // oversees everyone's. The same test the fetches use, so the link inherits
    // whatever the card counted.
    technicFilter () {
      return this.isTechnic ? { technicId: this.user.userId } : {}
    },
    // Where each card's "Vaata kõiki" goes: the page that owns the list, opened
    // on the card's own filter. Every one of these parameters is read back off
    // the query string by the page (see its `search`), so the list that draws
    // is the list the card counted.
    unpaidLink () {
      return { path: '/calendar/events', query: {
        ...UNPAID,
        // The page pins this itself (it only ever lists work orders).
        type: undefined,
        // The card counts what somebody still owes, so the one thing the page
        // must drop is the deleted entries it otherwise includes — and it has
        // to open on the oldest debt, the end of the list the card showed.
        deleted: false,
        order: 'oldest',
        ...this.technicFilter
      } }
    },
    // Contract objects: the server narrows to them anyway for a maintenance
    // window (it is the only type with a schedule), so the path says so too
    // rather than leaving the two to disagree.
    maintenenceLink () {
      return { path: '/object/list/contract', query: { ...MAINTENENCE, type: undefined, ...this.technicFilter } }
    },
    extraServiceLink () {
      return { path: '/object/list/all', query: { ...EXTRA_SERVICE, ...this.technicFilter } }
    },
    lastLoginsLink () {
      return { path: '/users/list', query: { ...LAST_LOGINS } }
    },
    nothingWaiting () {
      return !this.loading && !this.lastLoginsTotal && !this.unpaidTotal &&
        !this.nextExtraServiceTotal && !this.maintenenceTotal &&
        !this.undoneAktsTotal && !this.startedAktsTotal
    }
  },
  methods: {
    async refresh () {
      this.loading = true
      const calls = []

      try {
        // Layout asks for the feature list too, but a child mounts before its
        // parent does — waiting on that would mean deciding "module off" on
        // every single load. The answer is cached for five minutes, so this is
        // a request only the first time.
        await this.$store.dispatch('getFeatures')
        if (this.showLastLogins) {
          calls.push(this.getLastLogins())
        }
        if (this.showAkts) {
          calls.push(this.getAkts())
        }
        calls.push(this.getUnpaid())
        calls.push(this.getNextExtraServiceExpired())
        calls.push(this.getMaintenence())
        if (this.plate) {
          calls.push(this.getToday())
        }
        await Promise.all(calls)
      } catch (err) {
        // A card that could not be loaded shows 0 — which reads as "nothing
        // waiting" unless somebody says otherwise.
        this.$store.dispatch('showNotification', { text: 'Töölaua andmeid ei saanud laadida: ' + ((err && err.message) || 'ühenduse viga'), color: 'red' })
      } finally {
        this.loading = false
      }
    },
    // The four lists below still ask for a hundred rows even though eight are
    // drawn, and deliberately: none of the three endpoints can sort by what
    // these cards are about — /api/user and /api/calendar answer in their own
    // order and /api/object alphabetically — so the ranking is done here, and
    // it can only rank what it was sent. `count` is the true total either way,
    // which is what the card header shows.
    async getLastLogins () {
      const { users, count } = await api.getUsers({ ...LAST_LOGINS, limit: 100 })
      this.lastLogins = users.sort((a, b) => b.daysLastLogin - a.daysLastLogin)
      this.lastLoginsTotal = count
    },
    async getUnpaid () {
      const { events, count } = await api.getEvents({ ...UNPAID, limit: 100, ...this.technicFilter })
      this.unpaid = events.sort((a, b) => b.daysSinceEvent - a.daysSinceEvent)
      this.unpaidTotal = count
    },
    async getNextExtraServiceExpired () {
      const { objects, count } = await api.getObjects({ ...EXTRA_SERVICE, limit: 100, ...this.technicFilter })
      this.nextExtraService = objects
      this.nextExtraServiceTotal = count
    },
    async getMaintenence () {
      const { objects, count } = await api.getObjects({ ...MAINTENENCE, limit: 100, ...this.technicFilter })
      // Alphabetical is how the objects arrive, which is no use once only the
      // first eight are drawn: the ones already past their date come first.
      this.maintenence = objects.sort((a, b) =>
        ((a.latestInterval && a.latestInterval.daysTo) || 0) - ((b.latestInterval && b.latestInterval.daysTo) || 0))
      this.maintenenceTotal = count
    },
    // Both act lists come from one endpoint, one half each — asking for both at
    // once would pay for the calendar join twice. Who they cover is the
    // server's to decide: the office sees every technician's, a technician sees
    // his own, exactly as the cards above behave. A refusal leaves the counts
    // at zero, which takes the card off the page rather than breaking it.
    async getAkts () {
      const [ visits, started ] = await Promise.all([
        api.getUndoneAkts({ part: 'visits', days: UNDONE_AKT_DAYS, limit: this.rows }).catch(() => null),
        api.getUndoneAkts({ part: 'started', limit: this.rows }).catch(() => null)
      ])
      this.undoneAkts = (visits && visits.events) || []
      this.undoneAktsTotal = (visits && visits.total) || 0
      this.startedAkts = (started && started.started) || []
      this.startedAktsTotal = (started && started.startedTotal) || 0
    },
    // Straight into the form the visit would fill, the same link the work
    // order's own "Tee akt" button builds. It creates nothing: an act has no
    // row behind it until "Salvesta mustand".
    aktFormUrl (event) {
      const params = [ 'objectId=' + event.object.id, 'calendarId=' + event.id ]
      if (event.client) {
        params.push('clientId=' + event.client.id)
      }
      return '/akt/new?' + params.join('&')
    },
    // "Saatmata" covered two different jobs on two different buttons. The act
    // is finished only once it has gone out both ways, so the row names both
    // sends — the one already done and the one still owed — instead of making
    // the reader open the act to find out. The server lists an act here only
    // while one is missing.
    sendState (akt) {
      if (!akt.isSigned) return 'Mustand'
      // A Koondarve act is never sent to Merit on its own — the office adds it
      // to the month's arve — so it owes Merit nothing (models/akt.js isDone).
      const viaMerit = this.offersEnabled && !akt.monthlyBill
      const needsMerit = viaMerit && !akt.offerNo
      if (!akt.emailSentAt) {
        if (needsMerit) return 'E-post ja Merit saatmata'
        return viaMerit ? 'Meritisse andmed saadetud – e-post saatmata' : 'E-post saatmata'
      }
      return needsMerit ? 'E-post saadetud – Meritisse saatmata' : 'Saatmata'
    },
    // Whose work the row is. The dashboard reads across everyone's, so a row
    // that names only an address leaves the office opening the list page to
    // find out who to chase. A calendar row always carries a `user`, but only
    // the id of one where the query did not join the person: no name, nothing
    // to print, and no dangling separator either.
    eventTechnic (event) {
      return (event.user && event.user.fullName) || ''
    },
    objectAddress (event) {
      return (event.object && (event.object.address || event.object.fullAddress)) || 'Aadress puudub'
    },
    day (date) {
      return date ? dayjs(date).format('YYYY-MM-DD') : ''
    },
    // A row is tinted only where a per-row number tells it apart from its
    // neighbours; where every row means the same thing, the header count says
    // so instead and the list stays readable.
    overdueClass (days) {
      return days > OVERDUE_DAYS ? 'bg-red-lighten-4' : ''
    },
    // The new look's strip: today's work orders (a technician's own) and who
    // the planner has where today — active people only, as in Planeerija.
    // Quiet: without it the cards still stand.
    async getToday () {
      try {
        const date = dayjs().format('YYYY-MM-DD')
        const { events } = await api.getEvents({ startDate: date, endDate: date })
        const orders = events.filter(event => event.type === 2 &&
          (!this.isTechnic || event.user.id === this.user.userId || (event.helper && event.helper.id === this.user.userId)))
        const people = events
          .filter(event => event.type === 1 && event.workday && event.workday.name && event.user && event.user.active && event.user.fullName)
          .map(event => ({
            key: event.id,
            workday: event.workday.name,
            name: event.user.fullName,
            initials: initials(event.user.fullName),
            color: event.user.color || null
          }))
        this.today = { count: orders.length, people }
      } catch (err) {
        this.today = null
      }
    },
    viewEvent (event) {
      this.selectedEventId = event.id
      this.selectedEvent = true
    },
  },
  async mounted () {
    this.refresh()
  }
}
</script>
